
import React, { useEffect, useState } from 'react';
import {
  Box, Button, Chip, CircularProgress, Paper, Tooltip, Typography
} from '@mui/material';
import {
  Add, ArrowForward, AssessmentOutlined, BuildOutlined, CheckCircleOutline, CalculateOutlined,
  DeleteOutline, Inventory2Outlined, ListAltOutlined, SettingsOutlined,
  WarningAmberOutlined, LocationOnOutlined
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { apiFetch } from '../services/api';
import { useLogowanie } from '../context/KontekstLogowania';

const card = {
  border:'1px solid #e3eaf3',
  boxShadow:'0 8px 26px rgba(18,46,84,.055)',
  borderRadius:3,
  bgcolor:'#fff'
};

const STATUS_COLORS = {
  AKTYWNY:'#2f80ed',
  W_SERWISIE:'#f59e0b',
  WYCOFANY:'#ef5350',
  MAGAZYN:'#10b981',
  NOWY:'#7c3aed',
  USZKODZONY:'#e11d48',
  SPRZEDANY:'#64748b',
  ZLIKWIDOWANY:'#94a3b8'
};
const CAT_COLORS = ['#2f80ed','#7c3aed','#22c55e','#f59e0b','#e05276','#64748b'];

function formatMoney(v){
  return Number(v||0).toLocaleString('pl-PL',{minimumFractionDigits:2,maximumFractionDigits:2})+' zł';
}
function formatDate(v){
  if(!v) return '—';
  const d=new Date(v);
  return Number.isNaN(d.getTime()) ? String(v).slice(0,10) : d.toLocaleDateString('pl-PL');
}
function assetName(x){
  return [x.kategoria,x.producent,x.model].filter(Boolean).join(' ');
}
function StatusChip({value}) {
  const palette={
    AKTYWNY:{bg:'#e7f7ed',fg:'#23833d'},
    W_SERWISIE:{bg:'#fff1d6',fg:'#b66a00'},
    WYCOFANY:{bg:'#edf1f6',fg:'#586779'},
    MAGAZYN:{bg:'#eaf2ff',fg:'#1f5fbf'},
    NOWY:{bg:'#eef0ff',fg:'#5a50b5'},
    USZKODZONY:{bg:'#ffe8e8',fg:'#b3261e'}
  };
  const p=palette[value]||{bg:'#f0f3f7',fg:'#526173'};
  return <Chip size="small" label={value||'—'} sx={{bgcolor:p.bg,color:p.fg,fontWeight:800,fontSize:10}}/>;
}

function DonutChart({data,colors,total}) {
  const size=176, r=62, cx=88, cy=88, stroke=28;
  const actualSum=data.reduce((a,x)=>a+Number(x.liczba||0),0);
  const sum=actualSum || 1;
  let offset=0;
  const circ=2*Math.PI*r;

  return <Box sx={{display:'flex',alignItems:'center',gap:2.5,flexWrap:'wrap'}}>
    <Box sx={{position:'relative',width:size,height:size,flex:'0 0 auto'}}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="#eef3f8" strokeWidth={stroke}/>
        {actualSum > 0 && data.map((x,i)=>{
          const frac=Number(x.liczba||0)/sum;
          const dash=frac*circ;
          const el=<circle key={x.nazwa} cx={cx} cy={cy} r={r} fill="none"
            stroke={colors[i%colors.length]} strokeWidth={stroke}
            strokeDasharray={`${dash} ${circ-dash}`}
            strokeDashoffset={-offset}
            transform={`rotate(-90 ${cx} ${cy})`}/>;
          offset+=dash;
          return el;
        })}
      </svg>
      <Box sx={{position:'absolute',inset:0,display:'grid',placeItems:'center',textAlign:'center'}}>
        <Box>
          <Typography variant="h5" fontWeight={900}>{total ?? actualSum}</Typography>
          <Typography variant="caption" color="text.secondary">łącznie</Typography>
        </Box>
      </Box>
    </Box>

    <Box sx={{minWidth:180,flex:1}}>
      {data.slice(0,6).map((x,i)=>{
        const pct=actualSum ? (Number(x.liczba||0)/actualSum*100) : 0;
        return <Box key={x.nazwa} sx={{display:'grid',gridTemplateColumns:'12px 1fr auto',gap:1,alignItems:'center',mb:1}}>
          <Box sx={{width:9,height:9,borderRadius:'50%',bgcolor:colors[i%colors.length]}}/>
          <Typography variant="body2">{x.nazwa}</Typography>
          <Typography variant="body2" fontWeight={700}>{x.liczba} ({pct.toFixed(1)}%)</Typography>
        </Box>;
      })}
      {!data.length && <Typography variant="body2" color="text.secondary">Brak danych do wyświetlenia.</Typography>}
    </Box>
  </Box>;
}

function CardTitle({icon,title,subtitle,action}) {
  return <Box sx={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',gap:2,mb:1.7}}>
    <Box sx={{display:'flex',gap:1.2,alignItems:'flex-start'}}>
      {icon && <Box sx={{width:36,height:36,borderRadius:2,bgcolor:'#eef4ff',color:'#2364d8',display:'grid',placeItems:'center'}}>{icon}</Box>}
      <Box>
        <Typography variant="h6" fontWeight={850}>{title}</Typography>
        {subtitle && <Typography variant="caption" color="text.secondary">{subtitle}</Typography>}
      </Box>
    </Box>
    {action}
  </Box>;
}

function RecentCard({title,action,children}) {
  return <Paper sx={{...card,p:0,overflow:'hidden'}}>
    <Box sx={{display:'flex',justifyContent:'space-between',alignItems:'center',px:2.2,py:1.8,borderBottom:'1px solid #edf1f6'}}>
      <Typography variant="h6" fontWeight={850}>{title}</Typography>
      {action}
    </Box>
    <Box sx={{px:2.2,pb:1.2}}>{children}</Box>
  </Paper>;
}

function QuickAction({allowed=true,to,icon,label,sx}) {
  const button = <Button
    component={allowed ? Link : 'button'}
    to={allowed ? to : undefined}
    disabled={!allowed}
    variant="outlined"
    startIcon={icon}
    sx={{
      justifyContent:'flex-start',
      textAlign:'left',
      p:1.55,
      ...sx,
      ...(!allowed ? {
        bgcolor:'#f4f6f8',
        borderColor:'#d9e0e7',
        color:'#98a2b3',
        opacity:1,
        '&.Mui-disabled':{
          bgcolor:'#f4f6f8',
          borderColor:'#d9e0e7',
          color:'#98a2b3'
        }
      } : {})
    }}
  >
    {label}
  </Button>;

  return allowed ? button : <Tooltip title="Ta funkcja nie jest dostępna dla Twojej roli." arrow>{button}</Tooltip>;
}

export default function PanelGlowny(){
  const [d,setD]=useState(null);
  const { uzytkownik, maUprawnienie } = useLogowanie();

  useEffect(()=>{ apiFetch('/dashboard').then(setD); },[]);

  if(!d) return <CircularProgress/>;

  const p=d.podsumowanie||{};
  const statusData=(d.statusy||[]).map(x=>({nazwa:x.nazwa,liczba:Number(x.liczba||0)}));
  const catData=(d.kategorie||[]).map(x=>({nazwa:x.nazwa,liczba:Number(x.liczba||0)}));
  const maxLoc=Math.max(1,...(d.lokalizacje||[]).map(x=>Number(x.wartosc||0)));

  const kpis=[
    {label:'Łączna liczba środków',value:p.srodki||0,icon:<Inventory2Outlined/>,bg:'#eaf2ff',fg:'#2364d8'},
    {label:'Wartość początkowa',value:formatMoney(p.wartosc_poczatkowa),icon:<AssessmentOutlined/>,bg:'#e7f7ed',fg:'#22934c'},
    {label:'W użytkowaniu',value:p.aktywne||0,icon:<CheckCircleOutline/>,bg:'#eaf2ff',fg:'#2364d8'},
    {label:'W naprawie',value:p.w_serwisie||0,icon:<BuildOutlined/>,bg:'#fff2db',fg:'#dc8500'},
    {label:'Wycofane',value:p.wycofane||0,icon:<DeleteOutline/>,bg:'#ffe9e9',fg:'#d83a3a'}
  ];

  const roleLabel = {
    USER:'Użytkownik',
    OPERATOR:'Operator ewidencji',
    KSIEGOWY:'Księgowy',
    ADMIN:'Administrator'
  }[uzytkownik?.rola] || uzytkownik?.rola || 'Konto';

  return <Box>
    <Box sx={{mb:2.5,display:'flex',justifyContent:'space-between',alignItems:'flex-start',gap:2,flexWrap:'wrap'}}>
      <Box>
        <Typography variant="h4" fontWeight={900}>Panel główny</Typography>
        <Typography color="text.secondary">Podgląd najważniejszych danych.</Typography>
      </Box>
      <Box sx={{
        px:1.7,py:1.05,borderRadius:2,
        bgcolor:'#f5f8fc',
        border:'1px solid #dbe5f0',
        minWidth:{xs:'100%',sm:245}
      }}>
        <Typography variant="body2" sx={{fontWeight:900,color:'#173d73'}}>
          Witaj {uzytkownik?.imie} {uzytkownik?.nazwisko}
        </Typography>
        <Typography variant="caption" sx={{color:'#64748b',fontWeight:700}}>
          {roleLabel}
        </Typography>
      </Box>
    </Box>

    <Box sx={{display:'grid',gridTemplateColumns:{xs:'1fr',sm:'repeat(2,1fr)',xl:'repeat(5,1fr)'},gap:1.6,mb:2}}>
      {kpis.map(k=><Paper key={k.label} sx={{...card,p:2}}>
        <Box sx={{display:'flex',gap:1.5,alignItems:'flex-start'}}>
          <Box sx={{width:44,height:44,borderRadius:2.5,bgcolor:k.bg,color:k.fg,display:'grid',placeItems:'center'}}>{k.icon}</Box>
          <Box>
            <Typography variant="body2" color="text.secondary">{k.label}</Typography>
            <Typography variant="h5" fontWeight={900} sx={{mt:.3}}>{k.value}</Typography>
          </Box>
        </Box>
      </Paper>)}
    </Box>

    <Box sx={{display:'grid',gridTemplateColumns:{xs:'1fr',xl:'1fr 1fr 1.05fr'},gap:1.6,mb:1.6}}>
      <Paper sx={{...card,p:2.2}}>
        <CardTitle title="Środki wg statusu" subtitle="Podział według statusu."/>
        <DonutChart data={statusData} colors={statusData.map(x=>STATUS_COLORS[x.nazwa]||'#94a3b8')} total={p.srodki||0}/>
      </Paper>

      <Paper sx={{...card,p:2.2}}>
        <CardTitle title="Środki wg kategorii" subtitle="Podział według kategorii."/>
        <DonutChart data={catData} colors={CAT_COLORS} total={p.srodki||0}/>
      </Paper>

      <Paper sx={{...card,p:2.2}}>
        <CardTitle title="Szybkie akcje" subtitle="Najczęściej używane opcje."/>
        <Box sx={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:1.2}}>
          {uzytkownik?.rola==='KSIEGOWY' ? <QuickAction
            allowed={maUprawnienie('DEPRECIATION_VIEW')}
            to="/amortyzacja?widok=kalkulator"
            icon={<CalculateOutlined/>}
            label="Amortyzacja"
            sx={{bgcolor:'#eef9f2',borderColor:'#cdebd7',color:'#21833d'}}
          /> : <QuickAction
            allowed={maUprawnienie('ASSET_CREATE')}
            to="/srodki-trwale/dodaj"
            icon={<Add/>}
            label="Dodaj środek"
            sx={{bgcolor:'#eef9f2',borderColor:'#cdebd7',color:'#21833d'}}
          />}
          <QuickAction
            allowed={maUprawnienie('ASSET_VIEW')}
            to="/srodki-trwale"
            icon={<ListAltOutlined/>}
            label="Lista środków"
            sx={{bgcolor:'#edf4ff',borderColor:'#cddfff'}}
          />
          <QuickAction
            allowed={maUprawnienie('REPORT_VIEW')}
            to="/raporty"
            icon={<AssessmentOutlined/>}
            label="Generuj raport"
            sx={{bgcolor:'#f4efff',borderColor:'#ded2ff',color:'#6d43b7'}}
          />
          <QuickAction
            allowed={maUprawnienie('ADMIN_PANEL')}
            to="/administracja"
            icon={<SettingsOutlined/>}
            label="Panel administratora"
            sx={{bgcolor:'#fff6e7',borderColor:'#f7ddb1',color:'#ad6500'}}
          />
        </Box>

        <Box sx={{mt:1.8,p:1.5,bgcolor:'#f8fafc',border:'1px solid #edf1f6',borderRadius:2}}>
          <Typography variant="body2" fontWeight={850} sx={{mb:.35}}>Do sprawdzenia</Typography>
          <Typography variant="caption" color="text.secondary">
            Prośby o usunięcie: <b>{d.zadania?.oczekujace_usuniecia||0}</b> ·
            W serwisie: <b>{d.zadania?.w_serwisie||0}</b> ·
            Gwarancja ≤30 dni: <b>{d.zadania?.gwarancje_30||0}</b>
          </Typography>
        </Box>
      </Paper>
    </Box>

    <Box sx={{display:'grid',gridTemplateColumns:{xs:'1fr',lg:'1.08fr .92fr'},gap:1.6,mb:1.6}}>
      <Paper sx={{...card,p:2.2}}>
        <CardTitle
          icon={<WarningAmberOutlined fontSize="small"/>}
          title="Kończąca się gwarancja"
          subtitle="Najbliższe terminy."
        />
        {(d.gwarancje||[]).length ? (d.gwarancje||[]).map((x,i)=><Box key={x.id_srodka} sx={{
          display:'grid',gridTemplateColumns:{xs:'52px 1fr',sm:'52px 1fr 105px 72px'},gap:1,alignItems:'center',py:1.15,
          borderBottom:i<Math.min(4,(d.gwarancje||[]).length-1)?'1px solid #edf1f6':'none'
        }}>
          <Typography fontWeight={900} color="primary">{x.numer_unikatowy}</Typography>
          <Box>
            <Typography variant="body2" fontWeight={750}>{assetName(x)}</Typography>
            <Typography variant="caption" color="text.secondary">Koniec gwarancji: {formatDate(x.data_gwarancji_do)}</Typography>
          </Box>
          <Typography variant="body2" sx={{display:{xs:'none',sm:'block'}}}>{formatDate(x.data_gwarancji_do)}</Typography>
          <Chip size="small" label={`${x.pozostalo_dni} dni`} sx={{
            display:{xs:'none',sm:'flex'},
            bgcolor:Number(x.pozostalo_dni)<=30?'#ffe8e8':Number(x.pozostalo_dni)<=60?'#fff1d6':'#e7f7ed',
            color:Number(x.pozostalo_dni)<=30?'#b3261e':Number(x.pozostalo_dni)<=60?'#a86200':'#21833d',
            fontWeight:800,fontSize:10
          }}/>
        </Box>) : <Typography color="text.secondary" sx={{py:2}}>Brak zbliżających się końców gwarancji.</Typography>}
      </Paper>

      <Paper sx={{...card,p:2.2}}>
        <CardTitle
          icon={<LocationOnOutlined fontSize="small"/>}
          title="Wartość wg lokalizacji"
          subtitle="Wartość środków w lokalizacjach."
        />
        {(d.lokalizacje||[]).length ? (d.lokalizacje||[]).map(x=>{
          const pct=maxLoc ? Math.max(4,Number(x.wartosc||0)/maxLoc*100) : 4;
          return <Box key={x.nazwa} sx={{mb:1.45}}>
            <Box sx={{display:'flex',justifyContent:'space-between',gap:1,mb:.5}}>
              <Box>
                <Typography variant="body2" fontWeight={700}>{x.nazwa}</Typography>
                <Typography variant="caption" color="text.secondary">{x.liczba || 0} środków</Typography>
              </Box>
              <Typography variant="body2" fontWeight={850}>{formatMoney(x.wartosc)}</Typography>
            </Box>
            <Box sx={{height:8,bgcolor:'#eef3f8',borderRadius:99,overflow:'hidden'}}>
              <Box sx={{height:'100%',width:`${pct}%`,bgcolor:'#4a84ef',borderRadius:99}}/>
            </Box>
          </Box>;
        }) : <Typography color="text.secondary">Brak danych lokalizacyjnych.</Typography>}
      </Paper>
    </Box>

    <Box sx={{display:'grid',gridTemplateColumns:{xs:'1fr',xl:'1fr 1.15fr'},gap:1.6}}>
      <RecentCard title="Ostatnio dodane środki"
        action={<Button component={Link} to="/srodki-trwale" size="small" endIcon={<ArrowForward/>}>Zobacz wszystkie</Button>}>
        {(d.ostatnieSrodki||[]).slice(0,3).map((x,i)=><Box key={x.id_srodka} sx={{
          display:'grid',gridTemplateColumns:{xs:'52px 1fr',sm:'55px 1.6fr 95px 108px'},gap:1.2,alignItems:'center',
          py:1.25,borderBottom:i<Math.min(4,(d.ostatnieSrodki||[]).length-1)?'1px solid #edf1f6':'none'
        }}>
          <Typography fontWeight={900} color="primary">{x.numer_unikatowy}</Typography>
          <Box>
            <Typography variant="body2" fontWeight={750}>{assetName(x)}</Typography>
            <Typography variant="caption" color="text.secondary">{x.uzytkownik||'Nieprzypisany'} · {x.lokalizacja||'Brak lokalizacji'}</Typography>
          </Box>
          <Typography variant="body2" sx={{display:{xs:'none',sm:'block'}}}>{formatDate(x.data_przyjecia)}</Typography>
          <Box sx={{display:{xs:'none',sm:'block'}}}><StatusChip value={x.status}/></Box>
        </Box>)}
      </RecentCard>

      <RecentCard title="Ostatnie przesunięcia"
        action={<Button component={Link} to="/przesuniecia" size="small" endIcon={<ArrowForward/>}>Zobacz wszystkie</Button>}>
        {(d.ostatniePrzesuniecia||[]).slice(0,3).map((x,i)=><Box key={x.id_przesuniecia} sx={{
          display:'grid',gridTemplateColumns:{xs:'1fr',sm:'88px 1.2fr 1fr 1fr'},gap:1.2,alignItems:'center',
          py:1.25,borderBottom:i<Math.min(4,(d.ostatniePrzesuniecia||[]).length-1)?'1px solid #edf1f6':'none'
        }}>
          <Typography variant="body2" sx={{display:{xs:'none',sm:'block'}}}>{formatDate(x.data_przesuniecia)}</Typography>
          <Box>
            <Typography variant="body2" fontWeight={750}>{assetName(x)}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{display:{xs:'block',sm:'none'}}}>{formatDate(x.data_przesuniecia)}</Typography>
          </Box>
          <Typography variant="body2">{x.lokalizacja_z||'—'} → <b>{x.lokalizacja_do||'—'}</b></Typography>
          <Typography variant="body2">{x.uzytkownik_do||'—'}</Typography>
        </Box>)}
      </RecentCard>
    </Box>
  </Box>;
}
